import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, CalendarDays, CheckCircle2, Link2, Loader2, RefreshCw, Unlink } from 'lucide-react';

import { useToast } from '../../contexts/ToastContext';
import {
  disconnectGoogleCalendar,
  getGoogleCalendarConnections,
  retryGoogleCalendar,
  startGoogleCalendarOAuth,
  syncGoogleCalendar,
} from '../../services/googleCalendarApi';


const STATUS_LABELS = {
  active: 'Sincronização ativa',
  degraded: 'Há pendências de sincronização',
  reauth_required: 'É necessário reconectar a conta',
  disconnect_pending: 'Removendo eventos antes de desconectar',
  disconnected: 'Google Calendar não conectado',
};

const formatDateTime = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString('pt-BR');
};

const GoogleCalendarIntegrationSection = ({ professionals = [] }) => {
  const { toast } = useToast();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessUnavailable, setAccessUnavailable] = useState(false);
  const [busy, setBusy] = useState({});

  const activeProfessionals = useMemo(
    () => professionals.filter((item) => item?.id && item?.name && item?.is_active !== false),
    [professionals]
  );

  const loadConnections = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const response = await getGoogleCalendarConnections();
      setAccessUnavailable(false);
      setConnections(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessUnavailable(true);
        setConnections([]);
        return;
      }
      toast?.error?.(error.response?.data?.detail || 'Não foi possível consultar o Google Calendar.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadConnections(true);
  }, [loadConnections]);

  useEffect(() => {
    const hasPending = connections.some(
      (item) => item.pending_jobs > 0 || item.status === 'disconnect_pending'
    );
    if (!hasPending) return undefined;
    const interval = window.setInterval(() => loadConnections(false), 10000);
    return () => window.clearInterval(interval);
  }, [connections, loadConnections]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('google');
    if (result === 'connected') toast?.success?.('Google Calendar conectado com sucesso!');
    if (result === 'error') toast?.error?.('Não foi possível concluir a conexão com o Google.');
    if (result) {
      params.delete('google');
      const query = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    }
  }, [toast]);

  const withBusy = async (professionalId, action) => {
    setBusy((previous) => ({ ...previous, [professionalId]: true }));
    try {
      await action();
    } finally {
      setBusy((previous) => ({ ...previous, [professionalId]: false }));
    }
  };

  const connect = async (professionalId) => {
    await withBusy(professionalId, async () => {
      try {
        const response = await startGoogleCalendarOAuth(professionalId);
        window.location.assign(response.data.authorization_url);
      } catch (error) {
        toast?.error?.(error.response?.data?.detail || 'Não foi possível iniciar a conexão com o Google.');
      }
    });
  };

  const synchronize = async (professionalId) => {
    await withBusy(professionalId, async () => {
      try {
        const response = await syncGoogleCalendar(professionalId);
        toast?.success?.(response.data?.message || 'Sincronização iniciada.');
        await loadConnections(false);
      } catch (error) {
        toast?.error?.(error.response?.data?.detail || 'Não foi possível iniciar a sincronização.');
      }
    });
  };

  const retry = async (professionalId) => {
    await withBusy(professionalId, async () => {
      try {
        await retryGoogleCalendar(professionalId);
        toast?.success?.('Pendências reenfileiradas.');
        await loadConnections(false);
      } catch (error) {
        toast?.error?.(error.response?.data?.detail || 'Não foi possível tentar novamente.');
      }
    });
  };

  const disconnect = async (professionalId, removeEvents) => {
    const message = removeEvents
      ? 'Remover do Google os eventos criados pelo OdontoAssist e depois desconectar?'
      : 'Desconectar a conta? Os eventos já criados serão mantidos no Google Calendar.';
    if (!window.confirm(message)) return;
    await withBusy(professionalId, async () => {
      try {
        const response = await disconnectGoogleCalendar(professionalId, removeEvents);
        toast?.success?.(response.data?.message || 'Conta desconectada.');
        await loadConnections(false);
      } catch (error) {
        toast?.error?.(error.response?.data?.detail || 'Não foi possível desconectar a conta.');
      }
    });
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-gray-600"><Loader2 className="w-4 h-4 animate-spin" /> Carregando integrações...</div>;
  }

  if (accessUnavailable) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-purple-600" /> Google Calendar
        </h3>
        <div className="rounded-xl border border-purple-200 bg-purple-50 p-4 text-sm text-purple-900">
          Esta integração está em fase de testes e, neste momento, está disponível somente para usuários convidados.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-purple-600" /> Google Calendar
        </h3>
        <p className="mt-2 text-sm text-gray-600">
          Envie automaticamente os agendamentos profissionais do OdontoAssist para o calendário pessoal de cada profissional.
          Alterações e cancelamentos também serão refletidos no Google.
        </p>
        <p className="mt-2 text-xs text-gray-500">
          Esta integração é unidirecional: compromissos pessoais do Google não são importados para o OdontoAssist.
        </p>
      </div>

      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
        <h4 className="font-semibold text-blue-900">Como conectar</h4>
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-blue-900">
          <li>Clique em <strong>Conectar Google Calendar</strong> e escolha a conta Google do profissional.</li>
          <li>Avance pelas telas de autorização apresentadas pelo Google.</li>
          <li>
            Na tela de permissões, marque e autorize:
            <strong> “Ver, criar, mudar e excluir eventos nas suas agendas do Google”</strong>.
          </li>
          <li>Conclua em <strong>Continuar</strong>; você retornará automaticamente ao OdontoAssist.</li>
        </ol>
        <p className="mt-3 text-xs text-blue-800">
          Ao conectar, os agendamentos futuros já existentes também são enviados. Caso algum não apareça,
          use <strong>Sincronizar agora</strong> para fazer uma nova conferência.
        </p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
        <h4 className="font-semibold text-gray-900">Também funciona no iPhone</h4>
        <p className="mt-2 text-sm text-gray-700">
          Instale o aplicativo <strong>Google Agenda</strong> no iPhone e acesse a mesma conta Google conectada ao OdontoAssist.
          Se preferir usar o aplicativo <strong>Calendário</strong> da Apple, adicione essa conta Google em
          <strong> Ajustes → Apps → Calendário → Contas de Calendário → Adicionar Conta → Google</strong> e mantenha
          a opção <strong>Calendários</strong> ativada.
        </p>
        <p className="mt-2 text-xs text-gray-500">
          A integração envia os eventos para a conta Google; ela não cria eventos diretamente em uma agenda exclusiva do iCloud.
        </p>
      </div>

      {activeProfessionals.length === 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          Cadastre ao menos um profissional ativo antes de conectar o Google Calendar.
        </div>
      ) : (
        <div className="space-y-4">
          {activeProfessionals.map((professional) => {
            const connection = connections.find((item) => item.professional_id === professional.id) || {
              professional_id: professional.id,
              professional_name: professional.name,
              status: 'disconnected',
              connected: false,
              pending_jobs: 0,
              failed_jobs: 0,
            };
            const isBusy = busy[professional.id] === true;
            const hasError = ['degraded', 'reauth_required'].includes(connection.status);
            return (
              <div key={professional.id} className="rounded-xl border border-gray-200 p-4">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">{professional.name}</p>
                    <div className={`mt-1 flex items-center gap-2 text-sm ${hasError ? 'text-amber-700' : connection.connected ? 'text-green-700' : 'text-gray-500'}`}>
                      {hasError ? <AlertCircle className="w-4 h-4" /> : connection.connected ? <CheckCircle2 className="w-4 h-4" /> : <CalendarDays className="w-4 h-4" />}
                      {STATUS_LABELS[connection.status] || connection.status}
                    </div>
                    {connection.google_account_email && <p className="mt-1 text-sm text-gray-600">{connection.google_account_email}</p>}
                    {formatDateTime(connection.last_success_at) && <p className="mt-1 text-xs text-gray-500">Última sincronização: {formatDateTime(connection.last_success_at)}</p>}
                    {connection.pending_jobs > 0 && <p className="mt-1 text-xs text-purple-700">{connection.pending_jobs} sincronização(ões) pendente(s)</p>}
                    {connection.last_error_message && <p className="mt-2 max-w-xl text-xs text-amber-700">{connection.last_error_message}</p>}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {!connection.connected || connection.status === 'reauth_required' ? (
                      <button type="button" disabled={isBusy} onClick={() => connect(professional.id)} className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-60">
                        {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                        {connection.status === 'reauth_required' ? 'Reconectar Google' : 'Conectar Google Calendar'}
                      </button>
                    ) : (
                      <>
                        <button type="button" disabled={isBusy || connection.status === 'disconnect_pending'} onClick={() => synchronize(professional.id)} className="inline-flex items-center gap-2 rounded-lg border border-purple-500 px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-50 disabled:opacity-60">
                          <RefreshCw className={`w-4 h-4 ${isBusy ? 'animate-spin' : ''}`} /> Sincronizar agora
                        </button>
                        {connection.failed_jobs > 0 && <button type="button" disabled={isBusy} onClick={() => retry(professional.id)} className="rounded-lg border border-amber-500 px-3 py-2 text-sm text-amber-700 hover:bg-amber-50">Tentar novamente</button>}
                        <button type="button" disabled={isBusy || connection.status === 'disconnect_pending'} onClick={() => disconnect(professional.id, false)} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"><Unlink className="w-4 h-4" /> Desconectar</button>
                        <button type="button" disabled={isBusy || connection.status === 'disconnect_pending'} onClick={() => disconnect(professional.id, true)} className="rounded-lg border border-red-300 px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-60">Remover eventos e desconectar</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeProfessionals.length === 1 && !connections.some((item) => item.connected) && (
        <p className="text-xs text-gray-500">
          Como a clínica possui apenas um profissional ativo, ele já está selecionado automaticamente.
        </p>
      )}
    </div>
  );
};

export default GoogleCalendarIntegrationSection;
